import { writeBatch, doc, getDocs, collection, deleteField, type Firestore } from 'firebase/firestore';
import type { Chef, ChefOriginLanguage } from '../types';
import { extractInstagramUsername, inferChefOrigin, normalizeInstagramUsername, normalizeWebsiteUrl } from './chefDisplay';

function normalizeInstagramUserId(value: unknown): string | undefined {
    if (value === null || value === undefined) return undefined;
    const str = String(value).trim();
    return /^\d+$/.test(str) ? str : undefined;
}

const BATCH_SIZE = 400;

export const CHEF_CSV_HEADERS = [
    'id',
    'nickname',
    'name',
    'englishName',
    'originLanguage',
    'originCountry',
    'description',
    'instagramUsername',
    'instagramUserId',
    'website',
    'imageUrl',
    'disabled',
] as const;

export type ChefCsvHeader = (typeof CHEF_CSV_HEADERS)[number];

export function stripUndefinedFields<T extends Record<string, unknown>>(value: T): T {
    return Object.fromEntries(
        Object.entries(value).filter(([, fieldValue]) => fieldValue !== undefined),
    ) as T;
}

export function normalizeChefPayload(payload: Partial<Chef>): Partial<Chef> {
    const next: Partial<Chef> = { ...payload };

    const imageUrl = next.imageUrl?.trim();
    if (imageUrl) next.imageUrl = imageUrl;
    else delete next.imageUrl;

    if (Array.isArray(next.websites)) {
        const seen = new Set<string>();
        const cleaned = next.websites
            .map((w) => {
                if (typeof w !== 'string') return '';
                const trimmed = w.trim();
                if (!trimmed) return '';
                return normalizeWebsiteUrl(trimmed) ?? trimmed;
            })
            .filter((w) => {
                if (!w) return false;
                const key = w.toLowerCase();
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
        if (cleaned.length) {
            next.websites = cleaned;
            next.website = cleaned[0];
        } else {
            delete next.websites;
            delete next.website;
        }
    } else {
        delete next.websites;
        const website = next.website?.trim();
        if (website) next.website = normalizeWebsiteUrl(website) ?? website;
        else delete next.website;
    }

    const originCountry = next.originCountry?.trim();
    if (originCountry) next.originCountry = originCountry;
    else delete next.originCountry;

    const description = next.description?.trim();
    if (description) next.description = description;
    else delete next.description;

    let instagramUsername = normalizeInstagramUsername(next.instagramUsername) || undefined;
    if (!instagramUsername && next.websites) {
        for (const link of next.websites) {
            const fromLink = extractInstagramUsername(link);
            if (fromLink) {
                instagramUsername = fromLink;
                break;
            }
        }
    }
    if (instagramUsername) next.instagramUsername = instagramUsername;
    else delete next.instagramUsername;

    const instagramUserId = normalizeInstagramUserId(next.instagramUserId);
    if (instagramUserId) next.instagramUserId = instagramUserId;
    else delete next.instagramUserId;

    return stripUndefinedFields(next as Record<string, unknown>) as Partial<Chef>;
}

/** Builds a Firestore update object, deleting cleared link fields instead of leaving stale values. */
export function buildChefSaveUpdate(draft: Partial<Chef>): Record<string, unknown> {
    const hadWebsitesEditor = Array.isArray(draft.websites);
    const normalized = normalizeChefPayload({
        ...draft,
        website: undefined,
    });

    const update: Record<string, unknown> = {
        ...normalized,
        updatedAt: new Date().toISOString(),
    };

    if (hadWebsitesEditor && !normalized.websites?.length) {
        update.websites = deleteField();
        update.website = deleteField();
    }

    return update;
}

export interface ImportChefCsvResult {
    imported: number;
    updated: number;
    skipped: number;
    total: number;
}

function escapeCsvValue(value: string): string {
    if (/[",\n\r]/.test(value)) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
}

function parseCsvLine(line: string): string[] {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
            if (inQuotes && line[i + 1] === '"') {
                current += '"';
                i++;
            } else {
                inQuotes = !inQuotes;
            }
        } else if (char === ',' && !inQuotes) {
            values.push(current);
            current = '';
        } else {
            current += char;
        }
    }
    values.push(current);
    return values;
}

function parseCsv(text: string): Record<string, string>[] {
    const normalized = text.replace(/^\uFEFF/, '').trim();
    if (!normalized) return [];

    const lines = normalized.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length < 2) return [];

    const headers = parseCsvLine(lines[0]).map((h) => h.trim().toLowerCase());
    return lines.slice(1).map((line) => {
        const values = parseCsvLine(line);
        const row: Record<string, string> = {};
        headers.forEach((header, index) => {
            row[header] = (values[index] ?? '').trim();
        });
        return row;
    });
}

function slugifyNickname(value: string): string {
    return value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s]/g, '')
        .trim()
        .replace(/\s+/g, '_');
}

function parseBoolean(value: string | undefined): boolean | undefined {
    if (!value) return undefined;
    const v = value.toLowerCase();
    if (['true', 'yes', '1'].includes(v)) return true;
    if (['false', 'no', '0'].includes(v)) return false;
    return undefined;
}

function isEmptyValue(value: unknown): boolean {
    return value === undefined || value === null || value === '';
}

export function normalizeChefRow(row: Record<string, string>): Chef | null {
    const englishName = row.englishname || row.english_name || '';
    const rawName = row.name || '';
    const name = rawName.trim() || englishName.trim();
    const english = englishName.trim() || name;
    if (!english && !name) return null;

    const nickname = (row.nickname || row.id || slugifyNickname(english)).trim().toLowerCase();
    if (!nickname) return null;

    const id = (row.id || nickname).trim().toLowerCase();
    const originFromName = inferChefOrigin(name || english);
    const originLanguage = (row.originlanguage || row.origin_language || originFromName.originLanguage) as ChefOriginLanguage;
    const originCountry = row.origincountry || row.origin_country || originFromName.originCountry || '';
    const disabled = parseBoolean(row.disabled);
    const now = new Date().toISOString();

    return {
        id,
        nickname,
        name,
        englishName: english,
        originLanguage: originLanguage === 'he' ? 'he' : 'en',
        ...(originCountry ? { originCountry } : {}),
        ...(row.description ? { description: row.description } : {}),
        ...(row.instagramusername || row.instagram_username ? {
            instagramUsername: row.instagramusername || row.instagram_username,
        } : {}),
        ...(row.instagramuserid || row.instagram_user_id ? {
            instagramUserId: row.instagramuserid || row.instagram_user_id,
        } : {}),
        ...(row.website ? { website: row.website } : {}),
        ...(row.imageurl || row.image_url ? { imageUrl: row.imageurl || row.image_url } : {}),
        ...(disabled !== undefined ? { disabled } : {}),
        createdAt: now,
        updatedAt: now,
    };
}

export function chefToCsvRow(chef: Chef): string {
    const name = chef.name?.trim() || chef.englishName;
    return [
        chef.id,
        chef.nickname,
        name,
        chef.englishName,
        chef.originLanguage,
        chef.originCountry || '',
        chef.description || '',
        chef.instagramUsername || '',
        chef.instagramUserId || '',
        chef.website || '',
        chef.imageUrl || '',
        chef.disabled ? 'true' : 'false',
    ].map((v) => escapeCsvValue(String(v))).join(',');
}

export function exportChefsToCsv(chefs: Chef[]): string {
    const header = CHEF_CSV_HEADERS.join(',');
    const rows = [...chefs]
        .sort((a, b) => a.englishName.localeCompare(b.englishName))
        .map(chefToCsvRow);
    return [header, ...rows].join('\n');
}

export function downloadChefCsv(chefs: Chef[], filename?: string): void {
    const csv = exportChefsToCsv(chefs);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `mealpai-chefs-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
}

function mergeMissingChefFields(existing: Chef, incoming: Chef): Partial<Chef> | null {
    const merged: Partial<Chef> = {};
    let changed = false;

    const apply = <K extends keyof Chef>(field: K, value: Chef[K] | undefined) => {
        if (value === undefined || value === null || value === '') return;
        if (isEmptyValue(existing[field])) {
            merged[field] = value;
            changed = true;
        }
    };

    const resolvedName = incoming.name?.trim() || incoming.englishName?.trim() || '';
    const resolvedEnglish = incoming.englishName?.trim() || incoming.name?.trim() || '';

    apply('name', resolvedName || undefined);
    apply('englishName', resolvedEnglish || undefined);
    apply('nickname', incoming.nickname);
    apply('originLanguage', incoming.originLanguage);
    apply('originCountry', incoming.originCountry);
    apply('description', incoming.description);
    apply('instagramUsername', incoming.instagramUsername);
    apply('instagramUserId', incoming.instagramUserId);
    apply('website', incoming.website);
    apply('imageUrl', incoming.imageUrl);
    if (incoming.disabled !== undefined && existing.disabled === undefined) {
        merged.disabled = incoming.disabled;
        changed = true;
    }

    if (!existing.name?.trim() && (resolvedName || resolvedEnglish)) {
        merged.name = resolvedName || resolvedEnglish;
        changed = true;
    }

    if (changed) {
        merged.updatedAt = new Date().toISOString();
    }

    return changed ? merged : null;
}

export function parseChefCsv(text: string): Chef[] {
    return parseCsv(text)
        .map(normalizeChefRow)
        .filter((chef): chef is Chef => chef !== null);
}

export async function importChefsFromCsv(db: Firestore, csvText: string): Promise<ImportChefCsvResult> {
    const rows = parseChefCsv(csvText);
    const existingSnap = await getDocs(collection(db, 'chefs'));
    const existingById = new Map(existingSnap.docs.map((d) => [d.id, { ...d.data(), id: d.id } as Chef]));

    let imported = 0;
    let updated = 0;
    let skipped = 0;

    const creates: Chef[] = [];
    const updates: { id: string; data: Partial<Chef> }[] = [];

    for (const incoming of rows) {
        const existing = existingById.get(incoming.id) || existingById.get(incoming.nickname);
        if (existing) {
            const patch = mergeMissingChefFields(existing, incoming);
            if (patch) {
                updates.push({ id: existing.id, data: patch });
            } else {
                skipped++;
            }
        } else {
            creates.push(incoming);
        }
    }

    for (let i = 0; i < creates.length; i += BATCH_SIZE) {
        const chunk = creates.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        for (const chef of chunk) {
            batch.set(doc(db, 'chefs', chef.id), chef);
            imported++;
        }
        await batch.commit();
    }

    for (let i = 0; i < updates.length; i += BATCH_SIZE) {
        const chunk = updates.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        for (const { id, data } of chunk) {
            batch.update(doc(db, 'chefs', id), data);
            updated++;
        }
        await batch.commit();
    }

    return { imported, updated, skipped, total: rows.length };
}

export function buildNewChefDraft(language: ChefOriginLanguage = 'en'): Chef {
    const now = new Date().toISOString();
    return {
        id: '',
        name: '',
        englishName: '',
        nickname: '',
        originLanguage: language,
        disabled: false,
        createdAt: now,
        updatedAt: now,
    };
}

export function finalizeChefDraft(draft: Partial<Chef>): Chef | null {
    const englishName = draft.englishName?.trim() || draft.name?.trim() || '';
    if (!englishName) return null;

    const name = draft.name?.trim() || englishName;
    const nickname = draft.nickname?.trim() || slugifyNickname(englishName);
    const id = draft.id?.trim() || nickname;
    if (!id || !nickname) return null;

    const origin = inferChefOrigin(name);
    const now = new Date().toISOString();

    return normalizeChefPayload({
        id,
        nickname,
        name,
        englishName,
        originLanguage: draft.originLanguage || origin.originLanguage,
        originCountry: draft.originCountry?.trim() || origin.originCountry,
        description: draft.description,
        instagramUsername: draft.instagramUsername,
        instagramUserId: draft.instagramUserId,
        website: draft.website,
        websites: draft.websites,
        imageUrl: draft.imageUrl,
        disabled: !!draft.disabled,
        createdAt: draft.createdAt || now,
        updatedAt: now,
    }) as Chef;
}
